import React, { createContext, useContext, useEffect, useState } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import { HeaderTooltip } from "./HeaderTooltip.js";
import { createPortal } from "react-dom";
import type { InterfaceDesign } from "../../../util/src/design/visual-variants.js";
import { MacIcon } from "./MacIcon.js";
import { designTokens, type InterfaceColorMode } from "./tokens.js";
export const INTERFACE_DESIGN_KEY = "meet-manager.interface-design.v1";
const DesignContext = createContext<{
  mode: InterfaceDesign;
  setMode: (mode: InterfaceDesign) => void;
  colorMode: InterfaceColorMode;
  setColorMode: (mode: InterfaceColorMode) => void;
}>({
  mode: "original",
  setMode: () => {},
  colorMode: "light",
  setColorMode: () => {},
});
export const useInterfaceDesign = () => useContext(DesignContext);
export function InterfaceDesignProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [colorMode, updateColorMode] = useState<InterfaceColorMode>(() => {
    try {
      return localStorage.getItem("meet-manager.interface-color.v1") === "dark"
        ? "dark"
        : "light";
    } catch {
      return "light";
    }
  });
  const setColorMode = (next: InterfaceColorMode) => {
    updateColorMode(next);
    try {
      localStorage.setItem("meet-manager.interface-color.v1", next);
    } catch {
      /* Session-only choice. */
    }
  };
  const [mode, setMode] = useState<InterfaceDesign>(() => {
    try {
      return localStorage.getItem(INTERFACE_DESIGN_KEY) === "modern"
        ? "modern"
        : "original";
    } catch {
      return "original";
    }
  });
  const selectMode = (next: InterfaceDesign) => {
    setMode(next);
    try {
      localStorage.setItem(INTERFACE_DESIGN_KEY, next);
    } catch {
      /* Session-only choice if storage is unavailable. */
    }
  };
  return (
    <DesignContext.Provider
      value={{ mode, setMode: selectMode, colorMode, setColorMode }}
    >
      {mode === "modern" && (
        <GlobalStyles
          styles={{
            // Body zoom can expose the document canvas below the app chrome.
            "html, body": { backgroundColor: designTokens[colorMode].chrome },
          }}
        />
      )}
      {children}
    </DesignContext.Provider>
  );
}
/** Reserve header space for the interface controls. */
export function InterfaceDesignHeaderSlot() {
  const { mode } = useInterfaceDesign();
  return (
    <span
      data-interface-design-slot
      aria-hidden="true"
      style={{
        display: "block",
        width: mode === "modern" ? 64 : 160,
        height: 29,
        flexShrink: 0,
      }}
    />
  );
}

/** Keep recovery controls reachable when a scrolling header leaves the viewport. */
export function interfaceDesignAnchor(
  rect:
    | {
        left: number;
        right: number;
        top: number;
        bottom: number;
        width: number;
      }
    | undefined,
  viewport: { width: number; height: number },
) {
  return rect &&
    rect.width > 0 &&
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewport.height &&
    rect.right <= viewport.width
    ? { left: rect.left, top: rect.top, width: rect.width }
    : null;
}

/** The only write is the local preference. No reload, store replacement or keyed subtree. */
export function InterfaceDesignSwitch() {
  const { mode, setMode, colorMode, setColorMode } = useInterfaceDesign();
  const colors = designTokens[mode === "modern" ? colorMode : "light"];
  const [modalOpen, setModalOpen] = useState(false);
  const [anchor, setAnchor] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  useEffect(() => {
    let slot: HTMLElement | null = null;
    const updatePosition = () => {
      const rect = slot?.getBoundingClientRect();
      const next = interfaceDesignAnchor(rect, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      setAnchor((previous) =>
        previous?.left === next?.left &&
        previous?.top === next?.top &&
        previous?.width === next?.width
          ? previous
          : next,
      );
    };
    const resize = new ResizeObserver(updatePosition);
    const updateHost = () => {
      const modalVisible = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[role="dialog"], [aria-modal="true"], .MuiModal-root',
        ),
      ).some(
        (element) =>
          !element.closest('[aria-hidden="true"]') &&
          getComputedStyle(element).display !== "none",
      );
      setModalOpen(modalVisible);
      const nextSlot = document.querySelector<HTMLElement>(
        "[data-interface-design-slot]",
      );
      if (slot !== nextSlot) {
        resize.disconnect();
        slot = nextSlot;
        if (slot) {
          resize.observe(slot);
          if (slot.parentElement) resize.observe(slot.parentElement);
        }
      }
      updatePosition();
    };
    updateHost();
    const observer = new MutationObserver(updateHost);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-hidden", "aria-modal", "role"],
    });
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      observer.disconnect();
      resize.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, []);
  const toggle = () => setMode(mode === "modern" ? "original" : "modern");
  const buttonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    font: "inherit",
    height: 29,
    padding: "5px 8px",
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    background: colors.chrome,
    color: colors.text,
    cursor: "pointer",
    whiteSpace: "nowrap",
    outlineOffset: 2,
  };
  if (modalOpen) return null;
  return createPortal(
    <div
      data-interface-design={mode}
      style={{
        position: "fixed",
        ...(anchor
          ? {
              left: anchor.left,
              top: anchor.top,
              width: anchor.width,
              height: 29,
              justifyContent: "flex-end",
            }
          : {
              left: 16,
              bottom: 12,
              padding: "7px 10px",
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              background: colors.surface,
            }),
        zIndex: 1100,
        display: "flex",
        maxWidth: "calc(100vw - 32px)",
        gap: 6,
        alignItems: "center",
        color: colors.text,
        font: "11px system-ui",
      }}
    >
      {mode === "modern" && (
        <HeaderTooltip
          title={
            colorMode === "dark"
              ? "Passer au thème clair"
              : "Passer au thème sombre"
          }
          align="end"
        >
          <button
            type="button"
            aria-label={colorMode === "dark" ? "Mode clair" : "Mode sombre"}
            onPointerDown={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() =>
              setColorMode(colorMode === "dark" ? "light" : "dark")
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setColorMode(colorMode === "dark" ? "light" : "dark");
              }
            }}
            style={{
              ...buttonStyle,
              width: 29,
              padding: 5,
            }}
          >
            <MacIcon name={colorMode === "dark" ? "sun" : "moon"} size={15} />
          </button>
        </HeaderTooltip>
      )}
      <HeaderTooltip
        title={
          mode === "modern"
            ? "Revenir à l’interface d’origine"
            : "Essayer le nouveau design"
        }
        align="end"
      >
        <button
          type="button"
          data-design-switch
          aria-label={
            mode === "modern"
              ? "Revenir à l’interface d’origine"
              : "Essayer le nouveau design"
          }
          onPointerDown={(e) => e.preventDefault()}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggle}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggle();
            }
          }}
          style={
            mode === "modern"
              ? {
                  ...buttonStyle,
                  width: 29,
                  minWidth: 29,
                  padding: 5,
                  flexShrink: 0,
                }
              : buttonStyle
          }
        >
          <MacIcon name={mode === "modern" ? "undo" : "grid"} size={14} />
          {mode !== "modern" && "Nouveau design"}
        </button>
      </HeaderTooltip>
    </div>,
    document.body,
  );
}
/** Used only around the pilot. Healthy switches never remount descendants. */
export class DesignRecoveryBoundary extends React.Component<
  { children: React.ReactNode; onReturn: () => void; mode?: InterfaceDesign },
  { error: Error | null; mode?: InterfaceDesign }
> {
  state: { error: Error | null; mode?: InterfaceDesign } = {
    error: null,
    mode: this.props.mode,
  };
  static getDerivedStateFromProps(
    props: { mode?: InterfaceDesign },
    state: { mode?: InterfaceDesign },
  ): { mode?: InterfaceDesign; error: null } | null {
    return props.mode !== state.mode ? { mode: props.mode, error: null } : null;
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error && this.props.mode === "original")
      throw this.state.error;
    return this.state.error ? (
      <div role="alert">
        <p>Le nouveau rendu ne peut pas être affiché.</p>
        <button
          onClick={() => {
            this.props.onReturn();
            this.setState({ error: null });
          }}
        >
          Revenir à l’interface d’origine
        </button>
      </div>
    ) : (
      this.props.children
    );
  }
}

export function RecoverableDesign({ children }: { children: React.ReactNode }) {
  const { mode, setMode } = useInterfaceDesign();
  return (
    <DesignRecoveryBoundary mode={mode} onReturn={() => setMode("original")}>
      {children}
    </DesignRecoveryBoundary>
  );
}
